import axios from 'axios'
import type { RbacUser, RoleDefinition } from '../types'
import type { ProjectServicePort } from '../pages/projects/types'
import { parseGetStartedProjectAuthMode, type GetStartedProjectAuthMode } from './getstarted-contract'

export type { GetStartedProjectAuthMode } from './getstarted-contract'

export type AdcVersion = {
  version: string
  source_commit: string
  bundle_hash: string
  status: 'stable' | 'deprecated' | 'withdrawn'
  released_at: string | null
  file_count: number
  sections: string[]
}
export type AdcPolicy = { force_latest: boolean; pinned_version: string | null; policy_revision: number }
export type AdcPolicyRequest = { force_latest: boolean; pinned_version: string | null; expected_revision: number }
export type AdcProject = AdcPolicy & {
  delivery_mode?: 'database' | 'workspace'
  profile_revision?: number
  profile_version?: string
  profile_hash?: string
  project_id: string
  project_name: string
  project_tag: string
  target_version: string | null
  observed_versions: string[]
  status: 'unknown' | 'current' | 'historical' | 'pending' | 'mixed' | 'blocked' | 'stale'
  last_reported_at: string | null
  workspaces: {
    workspace_id: string
    ref_id: string
    version: string | null
    bundle_hash: string | null
    status: 'verified' | 'blocked' | 'unknown'
    error_code: string | null
    reported_at: string | null
    agent_version: string | null
  }[]
}
export type AdcDashboard = {
  latest_version: string | null
  versions: AdcVersion[]
  projects: AdcProject[]
  storage_mode: 'postgres'
  summary: {
    total_projects: number
    latest_projects: number
    historical_projects: number
    force_latest_projects: number
    unknown_projects: number
    blocked_projects: number
  }
}
export type AdcFile = { path: string; section: string; sha256: string; bytes: number }
export type AdcFileContent = { path: string; section: string; sha256: string; content: string }
export type AdcProfileFile = { path: string; sha256: string; content: string }
export type AdcProfilePublish = {
  version: string
  base_version: string
  source_commit: string
  source_note: string
  summary: string
  expected_revision: number
  activate: boolean
  bundle_hash: string
  amendments: { id: string; path: string }[]
  files: AdcProfileFile[]
}
export type AdcProfileRevision = Omit<AdcProfilePublish, 'files' | 'expected_revision' | 'activate'> & {
  project_id: string; revision: number; created_at: string; created_by: string; file_count: number
  is_active: boolean
}
export type AdcProfile = AdcProfileRevision & { files: AdcProfileFile[] }

export async function fetchAdcProfiles(project: string): Promise<{ revisions: AdcProfileRevision[] }> {
  return (await api.get(`/api/adc/projects/${encodeURIComponent(project)}/revisions`)).data
}
export async function fetchAdcProfile(project: string, revision: number): Promise<AdcProfile> {
  return (await api.get(`/api/adc/projects/${encodeURIComponent(project)}/revisions/${revision}`)).data
}
export async function publishAdcProfile(project: string, payload: AdcProfilePublish): Promise<AdcProfile> {
  return (await api.post(`/api/adc/projects/${encodeURIComponent(project)}/revisions`, payload)).data
}

export async function fetchAdcDashboard(): Promise<AdcDashboard> {
  return (await api.get('/api/adc/dashboard')).data
}
export async function fetchAdcVersions(): Promise<{ latest_version: string | null; versions: AdcVersion[] }> {
  return (await api.get('/api/adc/versions')).data
}
export async function saveAdcPolicy(projectId: string, policy: AdcPolicyRequest): Promise<AdcPolicy> {
  return (await api.put(`/api/adc/projects/${encodeURIComponent(projectId)}/policy`, policy)).data
}
export async function fetchAdcFiles(version: string): Promise<{ version: string; files: AdcFile[] }> {
  return (await api.get(`/api/adc/versions/${encodeURIComponent(version)}/files`)).data
}
export async function fetchAdcFile(version: string, path: string): Promise<AdcFileContent> {
  return (await api.get(`/api/adc/versions/${encodeURIComponent(version)}/file`, { params: { path } })).data
}

export type ProjectRow = {
  project_id: string
  has_api_key: boolean
  updated_at: string
  project_name?: string
  project_tag?: string
  project_path?: string
  project_owners?: string[]
  status?: string
  service_ports?: ProjectServicePort[]
  graph_total_nodes?: number
  graph_total_edges?: number
}

export type GuardianScan = {
  report_id: string
  project_id: string
  project_namespace?: string
  project_tag?: string
  account_email?: string
  source?: string
  rule_pack?: string
  repo_root?: string
  scan_started_at?: string
  scan_finished_at?: string
  stats?: Record<string, unknown>
  finding_count: number
  created_at?: string
}

export type GuardianFinding = {
  finding_id: string
  project_id: string
  report_id?: string
  status?: string
  severity?: string
  confidence?: string
  rule_id?: string
  cwe?: string
  owasp?: string
  file_path?: string
  line?: number | null
  sanitized_evidence?: string
  remediation?: string
  first_seen_at?: string
  last_seen_at?: string
}

export type GuardianScansResponse = {
  project_id?: string
  total: number
  storage_mode?: string
  storage_warning?: string | null
  scans: GuardianScan[]
}

export type GuardianFindingsResponse = {
  project_id: string
  total: number
  findings: GuardianFinding[]
}

export type ProjectFullIndexRequest = {
  repo_path: string
  strict_visibility?: boolean
  max_files?: number
}

export type ProjectIncrementalIndexRequest = {
  repo_path: string
  strict_visibility?: boolean
  mode?: 'git' | 'manual'
  changed_files?: string[]
}

export type LocalRdaIndexRequest = {
  project_id: string
  project_tag?: string
  source: 'auto' | 'full' | 'incremental'
  mode?: 'git' | 'manual'
  changed_files?: string[]
}

export type LocalRdaBrowserSession = {
  status: string
  bridge: string
  account_authenticated?: boolean
  token: string
}

export type LocalRdaJobStatus = {
  status: string
  job_id: string
  title?: string
  detail?: string
  started_at?: string
  updated_at?: string
  log_path?: string
}

export type ProjectIndexOperationResult = {
  status: string
  project_id: string
  source?: string
  run_id?: string
  queued_files?: number
  indexed_files?: number
  changed_files?: string[]
  requested_repo_path?: string
  resolved_repo_path?: string
  [key: string]: unknown
}

export type ProjectGraphStats = {
  status?: string
  source?: string
  graph_name?: string
  nodes?: {
    total?: number | null
    files?: number | null
    symbols?: number | null
    variables?: number | null
    [key: string]: unknown
  }
  edges?: {
    total?: number | null
    calls?: number | null
    flows?: number | null
    uses_variable?: number | null
    defines?: number | null
    contains?: number | null
    [key: string]: unknown
  }
  [key: string]: unknown
}

export type ProjectGraphNode = {
  id: string
  label: string
  type: string
  path?: string
  kind?: string
  edge_count?: number
}

export type ProjectGraphEdge = {
  id: string
  source: string
  target: string
  type: string
  label?: string
}

export type ProjectGraphChunk = {
  status?: string
  source?: string
  project_id: string
  graph_name?: string
  offset: number
  limit: number
  max_edges: number
  total_nodes: number
  total_edges: number
  returned_nodes: number
  returned_edges: number
  has_more: boolean
  next_offset: number | null
  nodes: ProjectGraphNode[]
  edges: ProjectGraphEdge[]
  timestamp?: string
}

export type ProjectIndexJobSummary = {
  source?: string
  files?: number | null
  symbols?: number | null
  run_id?: string | null
  repo_path?: string | null
  started_at?: string | null
  duration_seconds?: number | null
  [key: string]: unknown
}

export type ProjectIndexProgress = {
  project_id: string
  status: string
  run_id?: string | null
  processed_files?: number
  total_files?: number
  indexed?: number
  skipped?: number
  current_file?: string | null
  queue_position?: number | null
  elapsed_seconds?: number | null
  eta_seconds?: number | null
  started_at?: string | null
  completed_at?: string | null
  updated_at?: string | null
  requested_repo_path?: string | null
  resolved_repo_path?: string | null
  repo_path?: string | null
  graph_stats?: ProjectGraphStats | null
  last_full_index?: ProjectIndexJobSummary | null
  last_reindex?: ProjectIndexJobSummary | null
  [key: string]: unknown
}

export type ProjectIndexScheduleOption = {
  label: string
  value: number | string
}

export type IndexOptimizationMode = 'auto' | 'graph-delta' | 'hybrid-semantic' | 'large-monorepo' | 'docs-heavy'

export type ParsingStrategyMode = 'auto' | 'tree-sitter-ast' | 'scip-lsp-semantic' | 'ctags-symbols' | 'document-parser' | 'config-metadata'

export type ProjectIndexSchedule = {
  project_id: string
  enabled: boolean
  interval_hours: number
  mode: 'incremental' | 'full' | string
  strict_visibility: boolean
  max_files: number
  index_optimization_mode: IndexOptimizationMode
  parsing_strategy: ParsingStrategyMode
  interval_options: ProjectIndexScheduleOption[]
  mode_options: ProjectIndexScheduleOption[]
  next_run_at: string | null
  scheduler: {
    running?: boolean
    last_checked_at?: string | null
    last_run_at?: string | null
    last_success_at?: string | null
    last_status?: string | null
    last_error?: string | null
    last_run_id?: string | null
    last_mode?: string | null
    reason?: string | null
  }
}

export type AiFirstReadiness = {
  artifact: string
  version: string
  project_id: string
  readiness_level: string
  readiness_score?: number
  readiness_grade?: string
  signals: {
    index_status?: string
    indexed_file_count?: number
    ai_first_reference_present?: boolean
    project_overview_present?: boolean
    context_pack_available?: boolean
    [key: string]: unknown
  }
  blocking_gaps: string[]
  recommended_next_steps: string[]
}

export type AiFirstContextPackCandidate = {
  path: string
  source: string
  confidence: string
}

export type AiFirstPolicyContext = {
  model_grants_required?: boolean
  runtime_secrets_required?: boolean
  human_approval_required?: boolean
  sensitive_data?: boolean
  warnings?: string[]
  [key: string]: unknown
}

export type AiFirstEvidenceReference = {
  system?: string
  artifact?: string
  summary?: string
  [key: string]: unknown
}

export type AiFirstContextPack = {
  artifact: string
  version: string
  project_id: string
  task: string
  ai_first_stage: string
  token_budget: number
  target_paths: string[]
  target_symbols: string[]
  candidate_files: AiFirstContextPackCandidate[]
  missing_target_paths: string[]
  policy_context: AiFirstPolicyContext
  evidence_references: AiFirstEvidenceReference[]
  readiness_signals: Record<string, unknown>
  recommended_workflow: string[]
  warnings: string[]
}

export type AiFirstContextPackPayload = {
  task: string
  target_paths?: string[]
  target_symbols?: string[]
  token_budget?: number
  policy_context?: AiFirstPolicyContext
  evidence_references?: AiFirstEvidenceReference[]
}

export type RdMcpStatus = {
  status: 'available' | 'handoff_required' | 'degraded' | 'unavailable' | string
  mcp_server: string
  service?: string
  runtime: string
  health_url_configured: boolean
  account_authenticated?: boolean
  accountAuthenticated?: boolean
  checked_at: string
  detail: string
}

export type DirectoryBrowserEntry = {
  name: string
  path: string
  display_path?: string | null
  kind: 'directory'
  reveal_path?: string | null
}

export type DirectoryBrowserListing = {
  current_path: string
  current_display_path?: string | null
  current_reveal_path?: string | null
  path_context_label?: string | null
  parent_path: string | null
  roots: Array<{ name: string; path: string; display_path?: string | null; reveal_path?: string | null }>
  entries: DirectoryBrowserEntry[]
}

export type SecretKind = 'opaque_token' | 'symmetric_key' | 'hmac_key' | 'asymmetric_keypair'

export type SecretScopeType = 'project' | 'infrastructure' | 'host' | 'system'

export type ProjectSecret = {
  id: string
  project_id: string
  // Scope: what kind of thing owns the secret, and which instance. Added so a
  // credential that is not a project's (a build host's login, a hypervisor
  // account) has a correct home instead of a fake project.
  scope_type?: SecretScopeType
  scope_id?: string
  is_project_scoped?: boolean
  description?: string | null
  name: string
  permission: string
  secret_kind: SecretKind
  algorithm: string
  compliance_standard: string | null
  compliance_status: 'cnsa_compliant' | 'legacy'
  encoding: string
  fingerprint: string | null
  public_value: string | null
  version: number
  auto_rotate: boolean
  status: 'enabled' | 'disabled'
  expires_at: string | null
  created_at: string | null
  updated_at: string | null
  last_rotated_at: string | null
  secret_masked: string
}


// Generation policy. Every field mirrors the server default, so applying an
// untouched form changes nothing -- which is what makes the page safe to open.
export type SecretPolicy = {
  min_length_bytes: number
  max_length_bytes: number
  charset: string
  allow_special_chars: boolean
  default_algorithm: string
  default_expiry_days: number
  notify_lead_days: number
  notify_repeat_count: number
  auto_rotate_default: boolean
}

export type SecretPolicyPayload = SecretPolicy

export type SecretPolicyScopeRow = SecretPolicy & {
  scope_id: string
  updated_at?: string | null
  updated_by?: string | null
}

export type SecretVersionEntry = {
  version: number
  secret_kind: string | null
  algorithm: string | null
  encoding: string | null
  fingerprint: string | null
  public_value: string | null
  status: string | null
  expires_at: string | null
  created_at: string | null
  recorded_at: string | null
  recorded_by: string | null
  reason: string | null
}

export type SecretVersionHistory = {
  status: string
  secret_id: string
  current_version: number | null
  versions: SecretVersionEntry[]
}

export type SecretExpiryNotificationStatus = {
  enabled: boolean
  lead_days: number
  notify_repeat_count?: number
  repeat_count: number
  scan_interval_seconds: number
  recipient: string
  smtp_configured: boolean
  scheduler_running: boolean
}

export type SecretRotationStatus = {
  enabled: boolean
  lead_days: number
  lifetime_days: number
  scan_interval_seconds: number
  scheduler_running: boolean
}

export type LlmProviderHosting = 'local' | 'cloud'

export type LlmProvider = {
  id: string
  name: string
  hosting: LlmProviderHosting
  provider: string
  enabled: boolean
  endpoint_url: string
  model: string
  public_model: string | null
  upstream_model: string | null
  context_window: number | null
  timeout_seconds: number
  max_prompt_chars: number | null
  max_output_tokens: number | null
  temperature: number | null
  api_key_configured: boolean
  notes: string
  created_at: string | null
  updated_at: string | null
}

export type LlmAccessPolicyProvider = {
  id: string
  name: string
  hosting: LlmProviderHosting
  provider: string
  enabled: boolean
  model: string
  public_model: string | null
  api_key_configured: boolean
}

export type LlmAccessPolicy = {
  project_id: string
  provider_id: string
  enabled: boolean
  model_alias: string
  required_permission: 'llm.invoke.local' | 'llm.invoke.cloud'
  max_concurrent: number | null
  max_prompt_chars: number | null
  max_output_tokens: number | null
  monthly_token_quota: number | null
  allow_sensitive_data: boolean
  notes: string
  created_at: string | null
  updated_at: string | null
  provider?: LlmAccessPolicyProvider | null
}

export type UpsertLlmAccessPolicyPayload = {
  enabled: boolean
  model_alias?: string
  max_concurrent?: number | null
  max_prompt_chars?: number | null
  max_output_tokens?: number | null
  monthly_token_quota?: number | null
  allow_sensitive_data?: boolean
  notes?: string
}

export type UpsertLlmProviderPayload = {
  name: string
  hosting: LlmProviderHosting
  provider: string
  enabled: boolean
  endpoint_url: string
  model: string
  public_model?: string
  upstream_model?: string
  context_window?: number | null
  timeout_seconds: number
  max_prompt_chars?: number | null
  max_output_tokens?: number | null
  temperature?: number | null
  api_key?: string | null
  notes?: string
}

export type TtsProviderHosting = 'local' | 'cloud'

export type TtsProvider = {
  id: string
  name: string
  hosting: TtsProviderHosting
  provider: string
  enabled: boolean
  endpoint_url: string
  model: string
  public_model: string | null
  upstream_model: string | null
  default_voice: string | null
  output_format: string | null
  sample_rate: number | null
  timeout_seconds: number
  max_input_chars: number | null
  max_output_seconds: number | null
  api_key_configured: boolean
  notes: string
  created_at: string | null
  updated_at: string | null
}

export type TtsAccessPolicyProvider = {
  id: string
  name: string
  hosting: TtsProviderHosting
  provider: string
  enabled: boolean
  model: string
  public_model: string | null
  default_voice: string | null
  api_key_configured: boolean
}

export type TtsAccessPolicy = {
  project_id: string
  provider_id: string
  enabled: boolean
  model_alias: string
  voice_alias: string
  required_permission: 'tts.invoke.local' | 'tts.invoke.cloud'
  max_concurrent: number | null
  max_input_chars: number | null
  max_output_seconds: number | null
  monthly_character_quota: number | null
  allow_sensitive_data: boolean
  notes: string
  created_at: string | null
  updated_at: string | null
  provider?: TtsAccessPolicyProvider | null
}

export type UpsertTtsAccessPolicyPayload = {
  enabled: boolean
  model_alias?: string
  voice_alias?: string
  max_concurrent?: number | null
  max_input_chars?: number | null
  max_output_seconds?: number | null
  monthly_character_quota?: number | null
  allow_sensitive_data?: boolean
  notes?: string
}

export type UpsertTtsProviderPayload = {
  name: string
  hosting: TtsProviderHosting
  provider: string
  enabled: boolean
  endpoint_url: string
  model: string
  public_model?: string
  upstream_model?: string
  default_voice?: string
  output_format?: string
  sample_rate?: number | null
  timeout_seconds: number
  max_input_chars?: number | null
  max_output_seconds?: number | null
  api_key?: string | null
  notes?: string
}

export type AuditEvent = {
  event: string
  project_id: string
  detail: string
  timestamp: string
}

export type AuditEventsPage = {
  total: number
  limit: number
  offset: number
  returned: number
  has_more: boolean
  next_offset: number | null
  events: AuditEvent[]
}

export type PanelProfile = {
  display_name: string
  role: string
  workspace: string
  status: string
  storage_mode: string
  is_system_admin: boolean
  is_configured_admin?: boolean
  updated_at: string
  avatar_preset?: string
  avatar_background_color?: string
}

export type CurrentUserProfileUpdate = {
  avatar_preset?: string
  avatar_background_color?: string
}

export type AdminBackupResult = {
  status: string
  backup_id: string
  filename: string
  created_at: string
  storage_mode: string
  summary: {
    projects: number
    roles: number
    users: number
    secrets: number
    llm_providers?: number
    llm_access_policies?: number
    tts_providers?: number
    tts_access_policies?: number
    events: number
  }
  backup: Record<string, unknown>
}

export type AdminSqlBackupItem = {
  filename: string
  backup_guid: string
  created_at: string
  size_bytes: number
}

export type AdminSqlBackupScheduleOption = {
  label: string
  value: number
}

export type AdminSqlBackupSchedule = {
  enabled: boolean
  interval_hours: number
  retention_hours: number
  interval_options: AdminSqlBackupScheduleOption[]
  retention_options: AdminSqlBackupScheduleOption[]
  next_run_at: string | null
  scheduler: {
    running?: boolean
    last_checked_at?: string | null
    last_run_at?: string | null
    last_success_at?: string | null
    last_status?: string | null
    last_error?: string | null
    last_filename?: string | null
    deleted_expired?: string[]
    reason?: string | null
  }
}

export type AdminSqlRestoreResult = {
  status: string
  restored_at: string
  filename: string
  bytes_restored: number
  storage_mode: string
}

export type PanelNotification = {
  id: string
  title: string
  message: string
  timestamp: string
  severity: string
}

export type RbacPermission = {
  code: string
  shortCode?: string
  label: string
  scope: 'global' | 'project'
}

export type SupportVisitor = {
  name: string
  email: string
  external_id?: string
}

export type SupportChatAttachment = {
  id: string
  name: string
  content_type: string
  size?: number
  url?: string
  data_url?: string
}

export type SupportChatMessage = {
  message_id: string
  author_type: 'visitor' | 'agent' | 'system' | string
  author_name: string
  author_email: string
  body: string
  attachments: SupportChatAttachment[]
  metadata: Record<string, unknown>
  created_at: string
}

export type SupportLinkedTicket = {
  connector: string
  ticket_ref: string
  ticket_url: string
  ticket_number: string
}

export type SupportChatTyping = {
  author_type: 'visitor' | 'agent' | 'system' | string
  author_name: string
  author_email: string
  body: string
  is_typing: boolean
  updated_at: string
  expires_at: string
}

export type SupportChatSession = {
  session_id: string
  status: string
  visitor: SupportVisitor
  project_id: string
  site_id?: string
  site_name?: string
  source: string
  subject: string
  origin?: string
  page_url?: string
  referrer?: string
  metadata: Record<string, unknown>
  linked_ticket: SupportLinkedTicket | null
  typing: SupportChatTyping
  messages: SupportChatMessage[]
  created_at: string
  updated_at: string
}

export type SupportStatus = {
  status: string
  chat: {
    enabled: boolean
    storage: string
    durable?: boolean
    store_path_configured?: boolean
  }
  storage: SupportStorageStatus
  scheduler: SupportSchedulerStatus
  osticket: {
    enabled: boolean
    base_url: string
    api_key_configured: boolean
    timeout_seconds?: number
    bridge_path: string
    capabilities: string[]
  }
  llm?: SupportLlmSettings
  tawkto?: SupportTawktoSettings
  schedule?: SupportScheduleSettings
}

export type SupportStorageAreaStatus = {
  mode: string
  durable: boolean
}

export type SupportStorageStatus = {
  mode: string
  durable: boolean
  settings: SupportStorageAreaStatus
  chat_sessions: SupportStorageAreaStatus
  ticket_snapshots: SupportStorageAreaStatus
  llm_audits: SupportStorageAreaStatus
}

export type SupportSchedulerStatus = {
  enabled: boolean
  poll_seconds: number
  durable_queue: boolean
  running: boolean
}

export type SupportOsticketSettings = SupportStatus['osticket']

export type SupportScheduleTask = {
  task_id: string
  label: string
  description: string
  enabled: boolean
  interval_minutes: number
  state: 'open' | 'closed' | 'all' | string
  limit: number
  last_status: string
  last_message: string
  last_run_completed_at: string | null
  next_run_at: string | null
  depends_on?: string[]
  metrics: Record<string, number>
}

export type SupportScheduleAccountResult = {
  account_id: string
  account_name: string
  base_url?: string
  state?: string
  limit?: number
  status: string
  message: string
  ticket_count: number
  analyzed_count: number
  error_count: number
  [key: string]: unknown
}

export type SupportScheduleError = {
  account_id?: string
  account_name?: string
  status_code?: number
  message: string
  [key: string]: unknown
}

export type SupportScheduleSettings = {
  enabled: boolean
  interval_minutes: number
  state: 'open' | 'closed' | 'all' | string
  limit: number
  auto_analyze: boolean
  last_status: string
  last_message: string
  last_run_started_at: string | null
  last_run_completed_at: string | null
  next_run_at: string | null
  ticket_count: number
  analyzed_count: number
  error_count: number
  accounts: SupportScheduleAccountResult[]
  errors: SupportScheduleError[]
  tasks: SupportScheduleTask[]
  analysis_results?: SupportTicketAnalysisResult[]
}

export type SupportTicketAnalysisResult = {
  ticket_ref: string
  status: string
  provider: string
  model: string
  classification: string
  draft_reply_preview: string
  faq_matches: string[]
  review_signals: string[]
  [key: string]: unknown
}

export type SupportLlmProvider = 'local' | 'rd' | 'online' | 'openai' | 'openrouter' | 'deepseek' | 'gemini' | string

export type SupportLlmTaskProviders = {
  assist: SupportLlmProvider
  translate: SupportLlmProvider
}

export type SupportLlmSettings = {
  enabled: boolean
  provider: SupportLlmProvider
  local_base_url: string
  local_model: string
  online_provider: string
  online_base_url: string
  online_model: string
  online_api_key_configured: boolean
  rd_endpoint: string
  rd_project_id: string
  rd_model: string
  rd_api_key_configured: boolean
  timeout_seconds: number
  temperature: number
  task_providers: SupportLlmTaskProviders
  openai_enabled: boolean
  openai_base_url: string
  openai_model: string
  openai_api_key_configured: boolean
  openai_model_options?: string[]
  openrouter_enabled: boolean
  openrouter_base_url: string
  openrouter_model: string
  openrouter_api_key_configured: boolean
  openrouter_model_options?: string[]
  deepseek_enabled: boolean
  deepseek_base_url: string
  deepseek_model: string
  deepseek_api_key_configured: boolean
  deepseek_model_options?: string[]
  gemini_enabled: boolean
  gemini_base_url: string
  gemini_model: string
  gemini_api_key_configured: boolean
  gemini_model_options?: string[]
}

export type SupportWidgetSite = {
  site_id: string
  widget_key: string
  display_name: string
  project_id: string
  enabled: boolean
  allowed_origins: string[]
  theme: {
    title: string
    welcome_message: string
    accent_color: string
    position: string
  }
  routing: Record<string, unknown>
  abuse_protection?: SupportAbuseProtectionSettings
}

export type SupportAbuseProtectionSettings = {
  challenge_required: boolean
  challenge_provider: 'turnstile' | 'recaptcha' | 'disabled' | string
  provider?: string
  site_key: string
  spam_score_threshold: number
}

export type SupportSettings = {
  osticket: SupportOsticketSettings
  llm: SupportLlmSettings
  tawkto: SupportTawktoSettings
  widget_sites: SupportWidgetSite[]
  schedule: SupportScheduleSettings
}

export type SupportTawktoSettings = {
  enabled: boolean
  mcp_server_url: string
  mcp_server_token_configured: boolean
  mcp_server_timeout_seconds: number
  openapi_url: string
  openapi_spec_url: string
  openapi_token_configured: boolean
}

export type UpdateSupportSettingsPayload = {
  osticket?: {
    enabled: boolean
    base_url: string
    api_key?: string
    clear_api_key?: boolean
    timeout_seconds: number
    bridge_path: string
  }
  llm?: Partial<SupportLlmSettings> & {
    online_api_key?: string
    clear_online_api_key?: boolean
    rd_api_key?: string
    clear_rd_api_key?: boolean
    openai_api_key?: string
    clear_openai_api_key?: boolean
    openrouter_api_key?: string
    clear_openrouter_api_key?: boolean
    deepseek_api_key?: string
    clear_deepseek_api_key?: boolean
    gemini_api_key?: string
    clear_gemini_api_key?: boolean
  }
  tawkto?: {
    enabled: boolean
    mcp_server_url: string
    mcp_server_token?: string
    clear_mcp_server_token?: boolean
    mcp_server_timeout_seconds: number
    openapi_url: string
    openapi_spec_url: string
    openapi_token?: string
    clear_openapi_token?: boolean
  }
  widget_sites?: SupportWidgetSite[]
  schedule?: Partial<SupportScheduleSettings>
}

export type SupportOsticketTicket = {
  id?: number | string | null
  number?: string
  subject?: string
  status?: string | Record<string, unknown>
  state?: string | Record<string, unknown>
  priority?: string | Record<string, unknown>
  classification_category?: string
  classification_label?: string
  classification_confidence_pct?: number
  owner_name?: string
  owner_email?: string
  created?: string
  updated?: string
  account_id?: string
  account_name?: string
  account_base_url?: string
  attachment_count?: number
  has_attachments?: boolean
  entries?: Array<{
    id?: number | string | null
    type?: string
    title?: string
    poster?: string
    body?: string
    body_html?: string
    html_body?: string
    message_html?: string
    html?: string
    body_text?: string
    text?: string
    created?: string
    updated?: string
    attachments?: Array<Record<string, unknown>>
  }>
  attachments?: Array<Record<string, unknown>>
  [key: string]: unknown
}

export type SupportOsticketStaffOption = {
  staff_id: number
  name: string
  email: string
  default: boolean
}

export type SupportTicketAssistAction = 'draft_reply' | 'summarize' | 'translate'

export type SupportTicketAssistResponse = {
  status: string
  ticket_ref: string
  action: SupportTicketAssistAction
  provider: string
  model: string
  content: string
  cached?: boolean
  classification_category?: string
  classification_label?: string
  classification_confidence_pct?: number
  audit?: {
    operator_review_required?: boolean
    ticket_subject?: string
    message_count?: number
  }
}

export type SupportTicketSnapshot = {
  snapshot_id: string
  ticket_ref: string
  source: string
  ticket: SupportOsticketTicket | Record<string, unknown>
  metadata: Record<string, unknown>
  created_at: string
}

export type SupportLlmAuditRecord = {
  audit_id: string
  ticket_ref: string
  action: string
  provider: string
  model: string
  prompt_messages: Array<Record<string, unknown>>
  response_preview: string
  metadata: Record<string, unknown>
  operator_review_required: boolean
  created_at: string
}

export type SupportTicketContextSnippet = {
  path: string
  summary: string
  score?: number
  [key: string]: unknown
}

export type SupportTicketContext = {
  status: string
  ticket_ref: string
  project_id: string
  query: string
  recommended_queries: string[]
  snippets: SupportTicketContextSnippet[]
}

export type SupportWidgetSnippet = {
  status: string
  site_id: string
  widget_key: string
  script_url: string
  snippet: string
}

export type SupportScheduleRunResponse = {
  status: string
  source?: string
  schedule: SupportScheduleSettings
}

export function normalizePermissionCode(permission: string): string {
  return String(permission).trim().replace(/^\\+|\\+$/g, '')
}

export function normalizePermissionCodes(permissions: string[]): string[] {
  const normalized: string[] = []
  const seen = new Set<string>()

  for (const permission of permissions) {
    const code = normalizePermissionCode(permission)
    if (!code || seen.has(code)) {
      continue
    }
    seen.add(code)
    normalized.push(code)
  }

  return normalized
}

const LOCAL_PREVIEW_CONTROL_API_BASE_URL = 'http://192.168.1.240:18082'

function resolveLocalPreviewAdminApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const hostname = window.location.hostname
  const isLoopbackHost = hostname === '127.0.0.1' || hostname === 'localhost'
  const isViteLocalPort = /^(417\d|517\d)$/.test(window.location.port)

  if (isLoopbackHost && isViteLocalPort) {
    return LOCAL_PREVIEW_CONTROL_API_BASE_URL
  }

  return ''
}

function resolveAdminApiBaseUrl(): string {
  const explicit = (
    (import.meta.env.VITE_CONTROL_API_BASE_URL as string | undefined)
    ?? (import.meta.env.VITE_API_BASE_URL as string | undefined)
    ?? (import.meta.env.VITE_BACKEND_BASE_URL as string | undefined)
  )?.trim()
  if (explicit) {
    return explicit.replace(/\/$/, '')
  }

  const localPreviewBaseUrl = resolveLocalPreviewAdminApiBaseUrl()
  if (localPreviewBaseUrl) {
    return localPreviewBaseUrl
  }

  return ''
}

const api = axios.create({
  baseURL: resolveAdminApiBaseUrl(),
  timeout: 10000,
})

function resolveRdApiBaseUrl(): string {
  const explicit = (
    (import.meta.env.VITE_REPODEPOT_API_BASE_URL as string | undefined)
    ?? (import.meta.env.VITE_RD_API_BASE_URL as string | undefined)
  )?.trim()
  if (explicit) {
    return explicit
  }

  const adminBase = resolveAdminApiBaseUrl()

  try {
    const parsed = new URL(adminBase)
    if (parsed.port === '18082') {
      parsed.port = '18080'
    } else if (parsed.port === '8000') {
      parsed.port = '8080'
    }
    return parsed.toString().replace(/\/$/, '')
  } catch {
    return 'http://192.168.1.240:18080'
  }
}

const rdApi = axios.create({
  baseURL: resolveRdApiBaseUrl(),
  timeout: 10000,
})

const ADMIN_KEY_STORAGE = 'rd_admin_key'
const ADMIN_SESSION_STORAGE = 'rd_admin_session_token'
const ADMIN_EMAIL_STORAGE = 'rd_admin_email'
const ADMIN_REMEMBERED_EMAIL_STORAGE = 'rd_admin_remembered_email'
const ADMIN_SESSION_COOKIE = 'rd_admin_session'

let adminKey = ''
let adminSessionToken = sessionStorage.getItem(ADMIN_SESSION_STORAGE) ?? ''
let adminEmail = sessionStorage.getItem(ADMIN_EMAIL_STORAGE) ?? ''

function syncAdminSessionCookie(token: string): void {
  if (typeof document === 'undefined') {
    return
  }

  const normalizedToken = token.trim()
  if (normalizedToken) {
    const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${ADMIN_SESSION_COOKIE}=${encodeURIComponent(normalizedToken)}; Path=/; SameSite=Lax${secureAttribute}`
    return
  }

  document.cookie = `${ADMIN_SESSION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
}

if (adminSessionToken) {
  syncAdminSessionCookie(adminSessionToken)
}

// Global callback for handling session expiration
let onSessionExpired: (() => void) | null = null

export function setSessionExpiredCallback(callback: () => void): void {
  onSessionExpired = callback
}

export function getHttpStatusCode(error: unknown): number {
  return Number((error as { response?: { status?: number } })?.response?.status ?? 0)
}

export function expireAdminSession(): void {
  const hadSession = Boolean(
    adminSessionToken.trim() ||
      adminEmail.trim() ||
      (sessionStorage.getItem(ADMIN_SESSION_STORAGE) ?? '').trim() ||
      (sessionStorage.getItem(ADMIN_EMAIL_STORAGE) ?? '').trim(),
  )

  setAdminSessionToken('')
  setAdminEmail('')

  if (hadSession && onSessionExpired) {
    onSessionExpired()
  }
}

api.interceptors.request.use((config) => {
  adminSessionToken = sessionStorage.getItem(ADMIN_SESSION_STORAGE) ?? adminSessionToken

  config.headers = config.headers ?? {}

  if (adminSessionToken) {
    config.headers.Authorization = `Bearer ${adminSessionToken}`
  }
  if (adminKey) {
    config.headers['X-Admin-Key'] = adminKey
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const requestUrl = String(error?.config?.url ?? '')
      const authHeader = String(error?.config?.headers?.Authorization ?? '')
      const keyHeader = String(error?.config?.headers?.['X-Admin-Key'] ?? '')
      const detail = String(error?.response?.data?.detail ?? '').toLowerCase()

      const isLoginRequest = requestUrl.includes('/api/auth/login')
      const isCurrentUserFallbackCheck = requestUrl.includes('/api/rbac/users/me')
      const isSystemProfileValidation = requestUrl.includes('/api/system/profile')

      const keyRejected = detail.includes('x-admin-key') || detail.includes('admin key')
      const tokenRejected = detail.includes('token') || detail.includes('bearer') || detail.includes('session')

      if (
        !isLoginRequest &&
        !isCurrentUserFallbackCheck &&
        authHeader.toLowerCase().startsWith('bearer ') &&
        (isSystemProfileValidation || tokenRejected || !keyRejected)
      ) {
        expireAdminSession()
      }

      if (!isLoginRequest && !isCurrentUserFallbackCheck && keyHeader && keyRejected) {
        adminKey = ''
      }
    }
    return Promise.reject(error)
  },
)

export function setAdminKey(value: string): void {
  adminKey = value.trim()
}

export function getAdminKey(): string {
  return adminKey
}

export function setAdminSessionToken(value: string): void {
  adminSessionToken = value.trim()
  if (adminSessionToken) {
    sessionStorage.setItem(ADMIN_SESSION_STORAGE, adminSessionToken)
  } else {
    sessionStorage.removeItem(ADMIN_SESSION_STORAGE)
  }
  syncAdminSessionCookie(adminSessionToken)
}

export function getAdminSessionToken(): string {
  return adminSessionToken
}

export function setAdminEmail(value: string): void {
  adminEmail = value.trim().toLowerCase()
  if (adminEmail) {
    sessionStorage.setItem(ADMIN_EMAIL_STORAGE, adminEmail)
  } else {
    sessionStorage.removeItem(ADMIN_EMAIL_STORAGE)
  }
}

export function getAdminEmail(): string {
  return adminEmail
}

export function getGoogleLoginUrl(nextPath: string): string {
  const baseUrl = resolveAdminApiBaseUrl()
  const normalizedNext = nextPath.trim().startsWith('/') ? nextPath.trim() : '/auth/login'
  return `${baseUrl}/api/auth/google/login?next=${encodeURIComponent(normalizedNext)}`
}

export function setRememberedAdminEmail(value: string): void {
  const normalized = value.trim().toLowerCase()
  if (normalized) {
    localStorage.setItem(ADMIN_REMEMBERED_EMAIL_STORAGE, normalized)
  } else {
    localStorage.removeItem(ADMIN_REMEMBERED_EMAIL_STORAGE)
  }
}

export function getRememberedAdminEmail(): string {
  return localStorage.getItem(ADMIN_REMEMBERED_EMAIL_STORAGE) ?? ''
}

export function clearRememberedAdminEmail(): void {
  localStorage.removeItem(ADMIN_REMEMBERED_EMAIL_STORAGE)
}

export function isAdminAuthenticated(): boolean {
  // Re-sync from sessionStorage before checking. The in-memory token is only
  // guaranteed to reflect storage when it was last updated through
  // setAdminSessionToken(); without this refresh, a route guard evaluated in
  // the same page load right after storage changed underneath the running
  // app (e.g. a completed OAuth exchange in another code path, or a session
  // restored from storage) could see a stale empty in-memory value and
  // incorrectly treat an authenticated session as logged out.
  adminSessionToken = sessionStorage.getItem(ADMIN_SESSION_STORAGE) ?? adminSessionToken
  return Boolean(adminSessionToken.trim() || adminKey.trim())
}

export function clearAdminAuthState(): void {
  setAdminSessionToken('')
  setAdminKey('')
  setAdminEmail('')
}

export async function loginAdminSession(payload: {
  email: string
  password: string
  keep_logged_in: boolean
  authenticator_code?: string
  challenge_token?: string
}): Promise<{
  status: string
  token_type: string
  access_token: string
  expires_in: number
}> {
  const response = await api.post('/api/auth/login', payload)
  return response.data
}

export async function completeGoogleLogin(payload: { code: string }): Promise<{
  status: string
  token_type: string
  access_token: string
  expires_in: number
  display_name: string
  email: string
}> {
  const response = await api.post('/api/auth/google/complete', payload)
  return response.data
}

export async function fetchLoginContext(): Promise<{
  otp_bypass_eligible: boolean
  request_ip: string
  google_oauth: {
    configured: boolean
  }
  login_challenge: {
    required: boolean
    provider: string
    site_key: string
    configured: boolean
  }
}> {
  const response = await api.get('/api/auth/login-context')
  const challenge = response.data?.login_challenge ?? {}
  const googleOauth = response.data?.google_oauth ?? {}
  return {
    otp_bypass_eligible: Boolean(response.data?.otp_bypass_eligible),
    request_ip: String(response.data?.request_ip ?? ''),
    google_oauth: {
      configured: Boolean(googleOauth.configured),
    },
    login_challenge: {
      required: Boolean(challenge.required),
      provider: String(challenge.provider ?? ''),
      site_key: String(challenge.site_key ?? ''),
      configured: Boolean(challenge.configured),
    },
  }
}

export async function requestPasswordRecovery(payload: { email: string }): Promise<{ status: string; message: string }> {
  const response = await api.post('/api/auth/forgot-password', payload)
  return response.data
}

export async function requestPasswordChange(payload: {
  current_password: string
  new_password: string
}): Promise<{ status: string; message: string }> {
  const response = await api.post('/api/auth/change-password', payload)
  return response.data
}

export async function requestEmailChange(payload: {
  current_password: string
  new_email: string
}): Promise<{ status: string; message: string; new_email: string }> {
  const response = await api.post('/api/auth/change-email', payload)
  return response.data
}

export async function confirmEmailChange(payload: { verification_code: string; new_email: string }): Promise<{ status: string; message: string; new_email?: string }> {
  const response = await api.post('/api/auth/confirm-email', payload)
  return response.data
}


export async function fetchProjects(): Promise<ProjectRow[]> {
  const response = await api.get('/api/projects')
  return response.data.projects ?? []
}

export async function fetchGraphProjects(): Promise<ProjectRow[]> {
  const response = await api.get('/api/projects/operations/graph/projects')
  return response.data.projects ?? []
}

export async function fetchGuardianScans(projectId = '', limit = 100): Promise<GuardianScansResponse> {
  const trimmedProjectId = projectId.trim()
  const path = trimmedProjectId
    ? `/api/projects/${encodeURIComponent(trimmedProjectId)}/guardian/scans`
    : '/api/guardian/scans'
  const response = await api.get(path, { params: { limit } })
  return response.data
}

export async function fetchGuardianFindings(projectId: string, status = '', limit = 100): Promise<GuardianFindingsResponse> {
  const response = await api.get(`/api/projects/${encodeURIComponent(projectId)}/guardian/findings`, {
    params: { status, limit },
  })
  return response.data
}

export async function updateProject(
  projectId: string,
  payload: {
    project_name?: string
    project_tag?: string
    project_path?: string
    project_owners?: string[]
    status?: string
    service_ports?: ProjectServicePort[]
  },
): Promise<{ project_id: string; project_name?: string; project_tag?: string; project_path?: string; project_owners?: string[]; status?: string; service_ports?: ProjectServicePort[]; updated_at?: string }> {
  const response = await api.put(`/api/projects/${encodeURIComponent(projectId)}`, payload)
  return response.data
}

export async function createProject(
  projectId: string,
  payload: {
    project_name?: string
    project_tag?: string
    project_path?: string
    project_owners?: string[]
    status?: string
    service_ports?: ProjectServicePort[]
  },
): Promise<{ project_id: string; project_name?: string; project_tag?: string; project_path?: string; project_owners?: string[]; status?: string; service_ports?: ProjectServicePort[]; updated_at?: string }> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}`, payload)
  return response.data
}

export async function deleteProject(projectId: string): Promise<{ status: string; project_id: string }> {
  const response = await api.delete(`/api/projects/${encodeURIComponent(projectId)}`)
  return response.data
}

export async function ensureKey(projectId: string): Promise<{ project_id: string; api_key: string; status: string }> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/keys/ensure`)
  return response.data
}

export async function regenerateKey(projectId: string): Promise<{ project_id: string; api_key: string; status: string }> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/keys/regenerate`)
  return response.data
}

export async function revokeKey(projectId: string): Promise<{ status: string; project_id: string }> {
  const response = await api.delete(`/api/projects/${encodeURIComponent(projectId)}/keys`)
  return response.data
}

export async function runProjectFullIndex(
  projectId: string,
  payload: ProjectFullIndexRequest,
): Promise<ProjectIndexOperationResult> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/operations/index/full`, payload)
  return response.data
}

export async function runProjectIncrementalIndex(
  projectId: string,
  payload: ProjectIncrementalIndexRequest,
): Promise<ProjectIndexOperationResult> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/operations/index/incremental`, payload)
  return response.data
}

const localRdaBrowserBridgeBaseUrl = 'http://127.0.0.1:17658/rda/browser'

async function parseLocalRdaBridgeResponse(response: Response): Promise<any> {
  const text = await response.text()
  let payload: any = {}
  if (text.trim()) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = { detail: text }
    }
  }
  if (!response.ok) {
    throw new Error(String(payload.detail ?? payload.message ?? `Local RDA bridge returned HTTP ${response.status}`))
  }
  return payload
}

export async function fetchLocalRdaBrowserSession(): Promise<LocalRdaBrowserSession> {
  const response = await fetch(`${localRdaBrowserBridgeBaseUrl}/session`, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-store',
  })
  return await parseLocalRdaBridgeResponse(response)
}

export async function runLocalRdaProjectIndex(payload: LocalRdaIndexRequest): Promise<ProjectIndexOperationResult> {
  const session = await fetchLocalRdaBrowserSession()
  if (!session.token) {
    throw new Error('Local RDA bridge did not return a session token')
  }
  const response = await fetch(`${localRdaBrowserBridgeBaseUrl}/index`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'X-RDA-Bridge-Token': session.token,
    },
    body: JSON.stringify(payload),
  })
  const result = await parseLocalRdaBridgeResponse(response)
  return {
    ...result,
    project_id: payload.project_id,
    source: payload.source,
  }
}

export async function fetchLocalRdaJobStatus(jobId: string): Promise<LocalRdaJobStatus> {
  const session = await fetchLocalRdaBrowserSession()
  if (!session.token) {
    throw new Error('Local RDA bridge did not return a session token')
  }
  const response = await fetch(`${localRdaBrowserBridgeBaseUrl}/activity/${encodeURIComponent(jobId)}`, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-store',
    headers: {
      'X-RDA-Bridge-Token': session.token,
    },
  })
  return await parseLocalRdaBridgeResponse(response)
}

export async function fetchProjectIndexProgress(projectId: string): Promise<ProjectIndexProgress> {
  const response = await api.get(`/api/projects/${encodeURIComponent(projectId)}/operations/index/progress`, {
    timeout: 20000,
  })
  return response.data
}

export async function fetchProjectGraphChunk(
  projectId: string,
  params: { offset?: number; limit?: number; includeEdges?: boolean; maxEdges?: number; includeEdgeCounts?: boolean; search?: string } = {},
): Promise<ProjectGraphChunk> {
  const response = await api.get(`/api/projects/${encodeURIComponent(projectId)}/operations/graph/chunk`, {
    params: {
      offset: params.offset ?? 0,
      limit: params.limit ?? 250,
      include_edges: params.includeEdges ?? false,
      max_edges: params.maxEdges ?? 1000,
      include_edge_counts: params.includeEdgeCounts ?? false,
      search: params.search ?? '',
    },
  })
  return response.data
}

export async function fetchAiFirstReadiness(projectId: string): Promise<AiFirstReadiness> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/ai-first/readiness`)
  return response.data
}

export async function fetchAiFirstContextPack(
  projectId: string,
  payload: AiFirstContextPackPayload,
): Promise<AiFirstContextPack> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/ai-first/context-pack`, payload)
  return response.data
}

export async function fetchProjectIndexSchedule(projectId: string): Promise<ProjectIndexSchedule> {
  const response = await api.get(`/api/projects/${encodeURIComponent(projectId)}/operations/index/schedule`)
  return response.data
}

export async function updateProjectIndexSchedule(
  projectId: string,
  payload: {
    enabled: boolean
    interval_hours: number
    mode: 'incremental' | 'full'
    strict_visibility: boolean
    max_files: number
    index_optimization_mode?: IndexOptimizationMode
    parsing_strategy?: ParsingStrategyMode
  },
): Promise<ProjectIndexSchedule> {
  const response = await api.put(`/api/projects/${encodeURIComponent(projectId)}/operations/index/schedule`, payload)
  return response.data
}

export async function fetchRdMcpStatus(): Promise<RdMcpStatus> {
  const response = await api.get('/api/system/rd-mcp/status')
  return response.data
}

export async function fetchSupportStatus(): Promise<SupportStatus> {
  const response = await api.get('/api/support/status')
  return response.data
}

export async function fetchSupportSettings(): Promise<SupportSettings> {
  const response = await api.get('/api/support/settings')
  return response.data
}

export async function updateSupportSettings(payload: UpdateSupportSettingsPayload): Promise<SupportSettings> {
  const response = await api.put('/api/support/settings', payload)
  return response.data
}

export async function runSupportScheduleNow(): Promise<SupportScheduleRunResponse> {
  const response = await api.post('/api/support/schedule/run')
  return response.data
}

export async function fetchSupportTicketSnapshots(params: {
  ticketRef?: string
  limit?: number
} = {}): Promise<SupportTicketSnapshot[]> {
  const response = await api.get('/api/support/ticket-snapshots', {
    params: {
      ticket_ref: params.ticketRef || undefined,
      limit: params.limit ?? 50,
    },
  })
  return response.data.snapshots ?? []
}

export async function fetchSupportLlmAuditRecords(params: {
  ticketRef?: string
  limit?: number
} = {}): Promise<SupportLlmAuditRecord[]> {
  const response = await api.get('/api/support/audit/llm', {
    params: {
      ticket_ref: params.ticketRef || undefined,
      limit: params.limit ?? 50,
    },
  })
  return response.data.records ?? []
}

export async function fetchSupportWidgetSnippet(widgetKey: string): Promise<SupportWidgetSnippet> {
  const response = await api.get('/api/support/widget/snippet', {
    params: { widget_key: widgetKey },
  })
  return response.data
}

export async function fetchSupportChatSessions(params: { siteId?: string } = {}): Promise<SupportChatSession[]> {
  const response = await api.get('/api/support/chat/sessions', {
    params: {
      site_id: params.siteId || undefined,
    },
  })
  return response.data.sessions ?? []
}

export async function createSupportChatSession(payload: {
  visitor?: Partial<SupportVisitor>
  project_id?: string
  source?: string
  subject?: string
  initial_message?: string
  attachments?: SupportChatAttachment[]
  metadata?: Record<string, unknown>
}): Promise<{ session: SupportChatSession }> {
  const response = await api.post('/api/support/chat/sessions', payload)
  return response.data
}

export async function appendSupportChatMessage(
  sessionId: string,
  payload: {
    author_type: 'visitor' | 'agent' | 'system' | string
    body: string
    author_name?: string
    author_email?: string
    attachments?: SupportChatAttachment[]
    metadata?: Record<string, unknown>
  },
): Promise<{ message: SupportChatMessage }> {
  const response = await api.post(`/api/support/chat/sessions/${encodeURIComponent(sessionId)}/messages`, payload)
  return response.data
}

export async function linkSupportChatTicket(
  sessionId: string,
  payload: {
    connector: string
    ticket_ref: string
    ticket_url?: string
    ticket_number?: string
  },
): Promise<{ session: SupportChatSession }> {
  const response = await api.post(`/api/support/chat/sessions/${encodeURIComponent(sessionId)}/ticket-link`, payload)
  return response.data
}

export async function fetchSupportOsticketTickets(params: {
  state?: string
  limit?: number
  q?: string
  offset?: number
} = {}): Promise<SupportOsticketTicket[]> {
  const response = await api.get('/api/support/osticket/tickets', {
    params: {
      state: params.state ?? 'open',
      limit: params.limit ?? 100,
      q: params.q ?? '',
      offset: params.offset ?? 0,
    },
  })
  return response.data.tickets ?? []
}

export async function fetchSupportOsticketTicket(ticketRef: string): Promise<SupportOsticketTicket> {
  const response = await api.get(`/api/support/osticket/tickets/${encodeURIComponent(ticketRef)}`)
  return response.data.ticket ?? response.data
}

export async function fetchSupportOsticketTicketContext(
  ticketRef: string,
  params: { projectId?: string } = {},
): Promise<SupportTicketContext> {
  const response = await api.get(`/api/support/osticket/tickets/${encodeURIComponent(ticketRef)}/context`, {
    params: {
      project_id: params.projectId || undefined,
    },
  })
  return response.data
}

export async function fetchSupportOsticketStaff(): Promise<SupportOsticketStaffOption[]> {
  const response = await api.get('/api/support/osticket/staff')
  return response.data.staff ?? []
}

export async function createSupportOsticketTicket(payload: {
  subject: string
  message: string
  visitor?: Partial<SupportVisitor>
  source?: string
  metadata?: Record<string, unknown>
}): Promise<Record<string, unknown>> {
  const response = await api.post('/api/support/osticket/tickets', payload)
  return response.data
}

export async function assistSupportOsticketTicket(
  ticketRef: string,
  payload: {
    action: SupportTicketAssistAction
    language?: string
    instructions?: string
    force_refresh?: boolean
  },
): Promise<SupportTicketAssistResponse> {
  const response = await api.post(`/api/support/osticket/tickets/${encodeURIComponent(ticketRef)}/assist`, payload)
  return response.data
}

export async function replySupportOsticketTicket(
  ticketRef: string,
  payload: {
    message: string
    format?: 'text' | 'html'
    reply_to?: 'all' | 'user' | 'collabs' | 'none'
    alert?: boolean
    signature?: string
    staff_id?: number | null
  },
): Promise<Record<string, unknown>> {
  const response = await api.post(`/api/support/osticket/tickets/${encodeURIComponent(ticketRef)}/reply`, payload)
  return response.data
}

export async function actionSupportOsticketTicket(
  ticketRef: string,
  payload: {
    action: 'close' | 'resolve' | 'merge' | 'ban' | 'whitelist' | string
    reason?: string
    target_ticket_ref?: string
  },
): Promise<Record<string, unknown>> {
  const response = await api.post(`/api/support/osticket/tickets/${encodeURIComponent(ticketRef)}/action`, payload)
  return response.data
}

export async function fetchDirectoryListing(path?: string): Promise<DirectoryBrowserListing> {
  const response = await api.get('/api/system/path-browser', {
    params: path ? { path } : undefined,
  })
  return response.data
}

export async function fetchProjectSecrets(projectId: string): Promise<ProjectSecret[]> {
  const response = await api.get(`/api/projects/${encodeURIComponent(projectId)}/secrets`)
  return response.data.secrets ?? []
}

export async function createProjectSecret(
  projectId: string,
  payload: {
    name: string
    permission: string
    expires_at: string
    auto_rotate: boolean
    secret_value?: string
    secret_kind?: SecretKind
    algorithm?: string
    public_value?: string
  },
): Promise<{ status: string; secret?: ProjectSecret; secret_value?: string }> {
  const response = await api.post(`/api/projects/${encodeURIComponent(projectId)}/secrets`, payload)
  return response.data
}

export async function updateProjectSecret(
  projectId: string,
  secretId: string,
  payload: {
    status?: 'enabled' | 'disabled'
    permission?: string
    expires_at?: string
    auto_rotate?: boolean
    secret_value?: string
  },
): Promise<{ status: string; secret?: ProjectSecret }> {
  const response = await api.patch(`/api/projects/${encodeURIComponent(projectId)}/secrets/${encodeURIComponent(secretId)}`, payload)
  return response.data
}

export async function rotateProjectSecret(
  projectId: string,
  secretId: string,
): Promise<{ status: string; secret?: ProjectSecret; secret_value?: string }> {
  const response = await api.post(
    `/api/projects/${encodeURIComponent(projectId)}/secrets/${encodeURIComponent(secretId)}/rotate`,
  )
  return response.data
}

export async function deleteProjectSecret(projectId: string, secretId: string): Promise<{ status: string; secret_id?: string }> {
  const response = await api.delete(`/api/projects/${encodeURIComponent(projectId)}/secrets/${encodeURIComponent(secretId)}`)
  return response.data
}

// ---------------------------------------------------------------------------
// Secret generation policy
//
// The page needs the EFFECTIVE policy, not the raw stored row, so an operator
// can see what will actually be applied rather than which columns happen to be
// populated.
// ---------------------------------------------------------------------------

export async function fetchGlobalSecretPolicy(): Promise<{
  scope_id: string
  policy: SecretPolicy
  configured_scopes: SecretPolicyScopeRow[]
}> {
  const response = await api.get('/api/system/secret-policy')
  return response.data
}

export async function saveGlobalSecretPolicy(
  payload: SecretPolicyPayload,
): Promise<{ status: string; scope_id: string }> {
  const response = await api.put('/api/system/secret-policy', payload)
  return response.data
}

export async function deleteGlobalSecretPolicy(): Promise<{ status: string }> {
  const response = await api.delete('/api/system/secret-policy')
  return response.data
}

export async function fetchProjectSecretPolicy(
  projectId: string,
): Promise<{ project_id: string; scope_id: string; policy: SecretPolicy }> {
  const response = await api.get(
    `/api/projects/${encodeURIComponent(projectId)}/secret-policy`,
  )
  return response.data
}

export async function saveProjectSecretPolicy(
  projectId: string,
  payload: SecretPolicyPayload,
): Promise<{ status: string; scope_id: string }> {
  const response = await api.put(
    `/api/projects/${encodeURIComponent(projectId)}/secret-policy`,
    payload,
  )
  return response.data
}

export async function deleteProjectSecretPolicy(
  projectId: string,
): Promise<{ status: string }> {
  const response = await api.delete(
    `/api/projects/${encodeURIComponent(projectId)}/secret-policy`,
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Secret lifecycle: version history, notifications and rotation settings
// ---------------------------------------------------------------------------

export async function fetchSecretVersions(
  projectId: string,
  secretId: string,
): Promise<SecretVersionHistory> {
  const response = await api.get(
    `/api/projects/${encodeURIComponent(projectId)}/secrets/${encodeURIComponent(secretId)}/versions`,
  )
  return response.data
}

export async function restoreSecretVersion(
  projectId: string,
  secretId: string,
  version: number,
): Promise<{ status: string; secret?: ProjectSecret; restored_from_version?: number }> {
  const response = await api.post(
    `/api/projects/${encodeURIComponent(projectId)}/secrets/${encodeURIComponent(secretId)}/versions/${version}/restore`,
    {},
  )
  return response.data
}

export async function compareSecretFingerprint(
  projectId: string,
  fingerprint: string,
  secretName?: string,
): Promise<{
  status: string
  secret_id?: string
  secret_name?: string
  version?: number
}> {
  const response = await api.post(
    `/api/projects/${encodeURIComponent(projectId)}/secrets/compare-fingerprint`,
    { fingerprint, secret_name: secretName ?? null },
  )
  return response.data
}

export async function fetchSecretExpiryNotificationStatus(): Promise<SecretExpiryNotificationStatus> {
  const response = await api.get('/api/system/secret-expiry-notifications')
  return response.data
}

export async function fetchSecretsByScope(
  scopeType: SecretScopeType,
  scopeId?: string,
): Promise<{ count: number; secrets: ProjectSecret[] }> {
  const response = await api.get('/api/system/secrets/by-scope', {
    params: { scope_type: scopeType, scope_id: scopeId },
  })
  return response.data
}


export async function fetchLlmProviders(): Promise<LlmProvider[]> {
  const response = await api.get('/api/system/llm-providers')
  return response.data.providers ?? []
}

export async function upsertLlmProvider(
  providerId: string,
  payload: UpsertLlmProviderPayload,
): Promise<{ status: string; provider: LlmProvider }> {
  const response = await api.put(`/api/system/llm-providers/${encodeURIComponent(providerId)}`, payload)
  return response.data
}

export async function deleteLlmProvider(providerId: string): Promise<{ status: string; provider_id: string }> {
  const response = await api.delete(`/api/system/llm-providers/${encodeURIComponent(providerId)}`)
  return response.data
}

export async function fetchLlmAccessPolicies(): Promise<{
  total: number
  policies: LlmAccessPolicy[]
  providers: LlmAccessPolicyProvider[]
  projects: ProjectRow[]
}> {
  const response = await api.get('/api/system/llm-access-policies')
  return {
    total: Number(response.data?.total ?? 0),
    policies: response.data?.policies ?? [],
    providers: response.data?.providers ?? [],
    projects: response.data?.projects ?? [],
  }
}

export async function upsertLlmAccessPolicy(
  projectId: string,
  providerId: string,
  payload: UpsertLlmAccessPolicyPayload,
): Promise<{ status: string; policy: LlmAccessPolicy }> {
  const response = await api.put(
    `/api/system/llm-access-policies/${encodeURIComponent(projectId)}/${encodeURIComponent(providerId)}`,
    payload,
  )
  return response.data
}

export async function deleteLlmAccessPolicy(projectId: string, providerId: string): Promise<{ status: string; project_id: string; provider_id: string }> {
  const response = await api.delete(`/api/system/llm-access-policies/${encodeURIComponent(projectId)}/${encodeURIComponent(providerId)}`)
  return response.data
}

export async function fetchTtsProviders(): Promise<TtsProvider[]> {
  const response = await api.get('/api/system/tts-providers')
  return response.data.providers ?? []
}

export async function upsertTtsProvider(
  providerId: string,
  payload: UpsertTtsProviderPayload,
): Promise<{ status: string; provider: TtsProvider }> {
  const response = await api.put(`/api/system/tts-providers/${encodeURIComponent(providerId)}`, payload)
  return response.data
}

export async function deleteTtsProvider(providerId: string): Promise<{ status: string; provider_id: string }> {
  const response = await api.delete(`/api/system/tts-providers/${encodeURIComponent(providerId)}`)
  return response.data
}

export async function fetchTtsAccessPolicies(): Promise<{
  total: number
  policies: TtsAccessPolicy[]
  providers: TtsAccessPolicyProvider[]
  projects: ProjectRow[]
}> {
  const response = await api.get('/api/system/tts-access-policies')
  return {
    total: Number(response.data?.total ?? 0),
    policies: response.data?.policies ?? [],
    providers: response.data?.providers ?? [],
    projects: response.data?.projects ?? [],
  }
}

export async function upsertTtsAccessPolicy(
  projectId: string,
  providerId: string,
  payload: UpsertTtsAccessPolicyPayload,
): Promise<{ status: string; policy: TtsAccessPolicy }> {
  const response = await api.put(
    `/api/system/tts-access-policies/${encodeURIComponent(projectId)}/${encodeURIComponent(providerId)}`,
    payload,
  )
  return response.data
}

export async function deleteTtsAccessPolicy(projectId: string, providerId: string): Promise<{ status: string; project_id: string; provider_id: string }> {
  const response = await api.delete(`/api/system/tts-access-policies/${encodeURIComponent(projectId)}/${encodeURIComponent(providerId)}`)
  return response.data
}

export async function fetchProjectUsers(projectId: string): Promise<RbacUser[]> {
  const response = await api.get(`/api/rbac/projects/${encodeURIComponent(projectId)}/users`)
  return response.data.users ?? []
}

export async function fetchProjectRoles(projectId: string): Promise<RoleDefinition[]> {
  const response = await api.get(`/api/rbac/projects/${encodeURIComponent(projectId)}/roles`)
  return response.data.roles ?? []
}

export async function fetchRbacUsers(): Promise<RbacUser[]> {
  const response = await api.get('/api/rbac/users')
  return response.data.users ?? []
}

export async function fetchSystemUsers(): Promise<RbacUser[]> {
  const response = await api.get('/api/system/users')
  return response.data.users ?? []
}

export async function fetchCurrentUser(): Promise<RbacUser> {
  const response = await api.get('/api/rbac/users/me')
  return response.data.user
}

export async function createRbacUser(payload: {
  name: string
  email: string
  project_ids: string[]
  role_ids_by_project: Record<string, string[]>
}): Promise<RbacUser> {
  const response = await api.post('/api/rbac/users', payload)
  return response.data.user
}

export async function createSystemUser(payload: { name: string; email: string; role_id?: string; password?: string }): Promise<RbacUser> {
  const response = await api.post('/api/system/users', payload)
  return response.data.user
}

export async function updateRbacUser(
  userId: string,
  payload: {
    name: string
    email: string
  },
): Promise<{ status: string; user?: RbacUser; user_id?: string }> {
  const response = await api.put(`/api/rbac/users/${encodeURIComponent(userId)}`, payload)
  return response.data
}

export async function updateSystemUser(
  userId: string,
  payload: {
    name: string
    email: string
    role_id?: string
    password?: string
  },
): Promise<{ status: string; user?: RbacUser; user_id?: string }> {
  const response = await api.put(`/api/system/users/${encodeURIComponent(userId)}`, payload)
  return response.data
}

export async function deleteRbacUser(userId: string): Promise<{ status: string }> {
  const response = await api.delete(`/api/rbac/users/${encodeURIComponent(userId)}`)
  return response.data
}

export async function deleteSystemUser(userId: string): Promise<{ status: string }> {
  const response = await api.delete(`/api/system/users/${encodeURIComponent(userId)}`)
  return response.data
}

export async function updateProjectUserRoles(
  userId: string,
  projectId: string,
  roleIds: string[],
): Promise<{ status: string; user_id: string; project_id: string }> {
  const response = await api.post(`/api/rbac/users/${encodeURIComponent(userId)}/roles`, {
    project_id: projectId,
    role_ids: roleIds,
  })
  return response.data
}

export async function updateProjectUserProjects(
  userId: string,
  projectIds: string[],
): Promise<{ status: string; user_id: string; project_ids: string[] }> {
  const response = await api.post(`/api/rbac/users/${encodeURIComponent(userId)}/projects`, {
    project_ids: projectIds,
  })
  return response.data
}

export async function fetchAuditEventsPage(limit = 20, offset = 0): Promise<AuditEventsPage> {
  const response = await api.get('/api/audit/events', { params: { limit, offset } })
  const data = response.data ?? {}
  const events = data.events ?? []
  return {
    total: Number(data.total ?? events.length),
    limit: Number(data.limit ?? limit),
    offset: Number(data.offset ?? offset),
    returned: Number(data.returned ?? events.length),
    has_more: Boolean(data.has_more ?? false),
    next_offset: data.next_offset == null ? null : Number(data.next_offset),
    events,
  }
}

export async function fetchAuditEvents(limit = 20): Promise<AuditEvent[]> {
  return (await fetchAuditEventsPage(limit, 0)).events
}

export async function fetchPanelProfile(): Promise<PanelProfile> {
  const response = await api.get('/api/system/profile')
  return response.data
}

export async function saveCurrentUserProfile(payload: CurrentUserProfileUpdate): Promise<PanelProfile> {
  const response = await api.put('/api/panel/profile', payload)
  return response.data
}

export async function validateAdminSessionFromActivity(): Promise<boolean> {
  if (!getAdminSessionToken().trim()) {
    return false
  }

  try {
    await fetchPanelProfile()
    return true
  } catch (error) {
    if (getHttpStatusCode(error) === 401) {
      expireAdminSession()
      return false
    }

    return true
  }
}

export async function fetchPanelNotifications(limit = 5): Promise<PanelNotification[]> {
  const response = await api.get('/api/system/notifications', { params: { limit } })
  return response.data.notifications ?? []
}

export async function createAdminBackup(): Promise<AdminBackupResult> {
  const response = await api.post('/api/system/backup')
  return response.data
}

export async function createAdminSqlBackup(): Promise<{
  status: string
  filename: string
  backup_guid: string
  created_at: string
  size_bytes: number
}> {
  const response = await api.post('/api/system/backup/sql')
  return response.data
}

export async function fetchAdminSqlBackups(limit = 25): Promise<{ total: number; items: AdminSqlBackupItem[] }> {
  const response = await api.get('/api/system/backups', { params: { limit } })
  return response.data
}

export async function fetchAdminSqlBackupSchedule(): Promise<AdminSqlBackupSchedule> {
  const response = await api.get('/api/system/backup/schedule')
  return response.data
}

export async function updateAdminSqlBackupSchedule(payload: {
  enabled: boolean
  interval_hours: number
  retention_hours: number
}): Promise<AdminSqlBackupSchedule> {
  const response = await api.put('/api/system/backup/schedule', payload)
  return response.data
}

export async function deleteAdminSqlBackup(filename: string): Promise<{ status: string; filename: string }> {
  const response = await api.delete(`/api/system/backups/${encodeURIComponent(filename)}`)
  return response.data
}

export async function restoreAdminSqlBackupFromFile(payload: {
  filename: string
  confirm_text: string
}): Promise<AdminSqlRestoreResult> {
  const response = await api.post(`/api/system/backups/${encodeURIComponent(payload.filename)}/restore`, undefined, {
    params: {
      confirm_text: payload.confirm_text,
    },
  })
  return response.data
}

export async function restoreAdminSqlBackup(payload: {
  file: File
  confirm_text: string
}): Promise<AdminSqlRestoreResult> {
  const contentType = payload.file.type || (payload.file.name.toLowerCase().endsWith('.zip') ? 'application/zip' : 'application/sql')
  const response = await api.post('/api/system/backup/restore', payload.file, {
    params: {
      confirm_text: payload.confirm_text,
    },
    headers: {
      'Content-Type': contentType,
      'X-Backup-Filename': payload.file.name,
    },
  })
  return response.data
}

export async function fetchGetStartedProjectAuthMode(): Promise<GetStartedProjectAuthMode> {
  const response = await rdApi.get('/getstarted')
  return parseGetStartedProjectAuthMode(response.data)
}

export async function fetchRbacRoles(): Promise<{ total: number; roles: any[] }> {
  const response = await api.get('/api/rbac/roles')
  return {
    ...response.data,
    roles: (response.data.roles ?? []).map((role: any) => ({
      ...role,
      permissions: normalizePermissionCodes(Array.isArray(role.permissions) ? role.permissions : []),
    })),
  }
}

export async function fetchRbacPermissions(): Promise<{ total: number; permissions: RbacPermission[] }> {
  const response = await api.get('/api/rbac/permissions')
  return response.data
}

export async function createRbacRole(payload: {
  role_id: string
  name: string
  project_id: string
  permissions: string[]
}): Promise<{ status: string; message?: string; role?: any }> {
  const response = await api.post('/api/rbac/roles', {
    ...payload,
    permissions: normalizePermissionCodes(payload.permissions),
  })
  return {
    ...response.data,
    role: response.data.role
      ? {
          ...response.data.role,
          permissions: normalizePermissionCodes(Array.isArray(response.data.role.permissions) ? response.data.role.permissions : []),
        }
      : response.data.role,
  }
}

export async function updateRbacRole(
  roleId: string,
  payload: {
    role_id?: string
    name: string
    project_id?: string
    permissions: string[]
  }
): Promise<{ status: string; message?: string; role?: any }> {
  const response = await api.put(`/api/rbac/roles/${roleId}`, {
    ...payload,
    permissions: normalizePermissionCodes(payload.permissions),
  })
  return {
    ...response.data,
    role: response.data.role
      ? {
          ...response.data.role,
          permissions: normalizePermissionCodes(Array.isArray(response.data.role.permissions) ? response.data.role.permissions : []),
        }
      : response.data.role,
  }
}

export async function deleteRbacRole(roleId: string): Promise<{ status: string; message?: string }> {
  const response = await api.delete(`/api/rbac/roles/${roleId}`)
  return response.data
}

// ============================================================================
// User Security Settings
// ============================================================================

export interface TwoFASettings {
  email_2fa_enabled: boolean
  authenticator_2fa_enabled: boolean
}

export interface SocialLoginProvider {
  provider: string
  linked: boolean
  linked_email?: string
  linked_at?: string
}

export interface UserSecuritySettings {
  two_fa: TwoFASettings
  social_logins: SocialLoginProvider[]
}

export interface AuthenticatorSetupResponse {
  secret: string
  qr_code_url: string
  backup_codes: string[]
}

export async function getSecuritySettings(): Promise<UserSecuritySettings> {
  const response = await api.get('/api/user/security/settings')
  return response.data
}

export async function setupEmail2FA(enable: boolean): Promise<{ status: string; message: string }> {
  const response = await api.post('/api/user/security/2fa/email/setup', { enable })
  return response.data
}

export async function getAuthenticator2FASetup(): Promise<AuthenticatorSetupResponse> {
  const response = await api.get('/api/user/security/2fa/authenticator/setup')
  return response.data
}

export async function verifyAuthenticator2FA(
  secret: string,
  code: string
): Promise<{ status: string; message: string; backup_codes: string[] }> {
  const response = await api.post('/api/user/security/2fa/authenticator/verify', {
    secret,
    code,
  })
  return response.data
}

export async function disableAuthenticator2FA(): Promise<{ status: string; message: string }> {
  const response = await api.post('/api/user/security/2fa/authenticator/disable', {})
  return response.data
}

export async function getSocialLogins(): Promise<SocialLoginProvider[]> {
  const response = await api.get('/api/user/security/social-logins')
  return response.data
}

export async function linkSocialLogin(provider: string, auth_code: string): Promise<{ status: string; message: string }> {
  const response = await api.post('/api/user/security/social-logins/link', {
    provider,
    auth_code,
  })
  return response.data
}

export async function unlinkSocialLogin(provider: string): Promise<{ status: string; message: string }> {
  const response = await api.post('/api/user/security/social-logins/unlink', {
    provider,
  })
  return response.data
}
